import React, { useContext } from 'react';
import { useMsal } from '@azure/msal-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { TranscriptionContext } from '../context/TranscriptionContext';
import useIsAdmin from '../hooks/useIsAdmin';

const BRAND_GRADIENT = 'linear-gradient(135deg, #7A9A01 0%, #8AAE02 100%)';
const BRAND_SHADOW = '0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.08)';

const NAV_ITEMS = [
  { path: '/', label: 'Start', authRequired: false },
  { path: '/transcribe', label: 'Audios Transkribieren', authRequired: true },
  { path: '/transcriptions', label: 'Transkriptionen anzeigen', authRequired: true },
  { path: '/settings/oneclick', label: '1-Click KI-Funktionen', authRequired: true },
  // Admin-Bereich mit Reitern (Statistiken, API-Schlüssel) unter /admin/…
  { path: '/admin', label: 'Admin', authRequired: true, adminOnly: true, prefix: true },
];

function NavLink({ item, active, onClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        background: hover && !active ? 'rgba(255,255,255,0.12)' : 'transparent',
        color: '#fff',
        border: 'none',
        cursor: 'pointer',
        padding: '10px 14px 12px',
        fontSize: '0.9rem',
        fontFamily: 'inherit',
        fontWeight: active ? 600 : 500,
        letterSpacing: '0.01em',
        borderRadius: '6px',
        transition: 'background-color 140ms ease',
      }}
    >
      {item.label}
      <span
        aria-hidden
        style={{
          position: 'absolute',
          left: 12,
          right: 12,
          bottom: 4,
          height: 2,
          borderRadius: 2,
          backgroundColor: active ? '#fff' : 'transparent',
          transition: 'background-color 140ms ease',
        }}
      />
    </button>
  );
}

function NavigationBar() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const location = useLocation();
  const { clearTranscription } = useContext(TranscriptionContext);
  const isLoggedIn = instance.getAllAccounts().length > 0;
  const isAdmin = useIsAdmin();

  const logout = async () => {
    try {
      // SEC-037: Session-Daten aufraeumen, BEVOR der MSAL-Logout-Redirect
      // läuft. Sonst zeigt der Job-Queue-Overlay auf der Login-Page noch
      // Dateinamen + Job-IDs des vorherigen Users (Info-Leak auf shared workstations).
      clearTranscription();

      const currentAccount = instance.getAllAccounts()[0];
      if (currentAccount && instance) {
        await instance.logoutRedirect({ account: currentAccount });
      }
    } catch (error) {
      console.error('Fehler beim Abmelden:', error);
    }
  };

  const visibleItems = NAV_ITEMS.filter(
    (item) => (!item.authRequired || isLoggedIn) && (!item.adminOnly || isAdmin),
  );

  return (
    <div
      style={{
        background: BRAND_GRADIENT,
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        padding: '6px 16px',
        boxShadow: BRAND_SHADOW,
        position: 'relative',
        zIndex: 2,
      }}
    >
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {visibleItems.map((item) => (
          <NavLink
            key={item.path}
            item={item}
            active={
              item.prefix
                ? location.pathname.startsWith(`${item.path}/`) || location.pathname === item.path
                : location.pathname === item.path
            }
            onClick={() => navigate(item.path)}
          />
        ))}
      </div>

      {isLoggedIn && (
        <button
          type="button"
          onClick={logout}
          style={{
            marginLeft: 'auto',
            backgroundColor: 'rgba(255,255,255,0.14)',
            color: '#fff',
            padding: '7px 14px',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '0.85rem',
            fontFamily: 'inherit',
            letterSpacing: '0.02em',
            transition: 'background-color 140ms ease',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.24)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)';
          }}
        >
          Logout
        </button>
      )}
    </div>
  );
}

export default NavigationBar;
